// CrewAtoms.js — shared presentational atoms for the Crew Dashboard.
import React from 'react';
import { T, CLOCK, BUDGET_CATS } from '../lib/crewTokens';
import { simNow, hhmm, fmtMin, fmtFt, fmtDur } from '../lib/crewFormat';

const KT = T;
const KCLOCK = CLOCK;
const kSimNow = simNow;

// ── statusAccent — shared status→color helper (used by all views) ──
function statusAccent(level) { return level === 'critical' ? T.rust : level === 'watch' ? T.warn : T.green; }

// ── Crew health glyph — saguaro / agave, 3 states ────────────
function CrewGlyph({ level, variant = 'saguaro', size = 18, style }) {
  const color = level === 'critical' ? KT.rust : level === 'watch' ? KT.warn : KT.green;
  const common = { fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };
  let art;
  if (variant === 'agave') {
    art = level === 'healthy' ? (
      <g {...common}><path d="M10 16 V6" /><path d="M10 14 C7.5 13 6 11 5.2 8.5" /><path d="M10 14 C12.5 13 14 11 14.8 8.5" /><path d="M6.5 17 H13.5" /></g>
    ) : level === 'watch' ? (
      <g {...common}><path d="M10 16 V7" /><path d="M10 14 C7.5 13 6 11 5.2 8.5" /><path d="M10 14 C12.3 13.6 14 13.8 15 15.4" /><path d="M6.5 17 H13.5" /></g>
    ) : (
      <g {...common}><path d="M10 16 C10 13.5 10.6 11.8 12.2 10.6" /><path d="M10 14 C8.2 14.4 6.8 15.3 6.2 16.6" /><path d="M10 13.6 C11.9 14 13.3 15 13.8 16.4" /><path d="M6.5 17 H13.5" /></g>
    );
  } else {
    art = level === 'healthy' ? (
      <g {...common}><path d="M10 17 V5" /><path d="M10 11 H7 a1 1 0 0 1 -1 -1 V7" /><path d="M10 9 H13 a1 1 0 0 0 1 -1 V5.5" /><path d="M6.5 17 H13.5" /></g>
    ) : level === 'watch' ? (
      <g {...common}><path d="M10 17 V6" /><path d="M10 11 H7 a1 1 0 0 1 -1 -1 V8" /><path d="M10 9 C12 9 13.6 9.8 14 12" /><path d="M6.5 17 H13.5" /></g>
    ) : (
      <g {...common}><path d="M10 17 V7.5" /><path d="M10 11 C8.2 11.2 7 12.4 6.8 14.2" /><path d="M10 9.5 C11.8 9.7 13 10.8 13 12.6" /><path d="M6.5 17 H13.5" /></g>
    );
  }
  return <svg width={size} height={size} viewBox="0 0 20 20" style={style} aria-label={`status: ${level}`}>{art}</svg>;
}

// ── Live duration — ticks every second off the sim clock ─────
function useTick(ms = 1000) {
  const [, set] = React.useState(0);
  React.useEffect(() => { const id = setInterval(() => set((n) => n + 1), ms); return () => clearInterval(id); }, [ms]);
}
function LiveDuration({ since, withSec = false, style }) {
  useTick(withSec ? 1000 : 30000);
  if (since == null) return <span style={style}>—</span>;
  return <span style={style}>{fmtDur(kSimNow() - since, withSec)}</span>;
}

// ── Avatar (initials) ────────────────────────────────────────
function Avatar({ name, initials, color, size = 28, ring }) {
  return (
    <span title={name} style={{
      width: size, height: size, borderRadius: '50%', background: color, color: '#fbf8f1',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      fontSize: size * 0.38, fontWeight: 800, letterSpacing: '0.01em',
      boxShadow: ring ? `0 0 0 2px ${KT.surface}, 0 0 0 3.5px ${color}` : 'none',
    }}>{initials}</span>
  );
}

// ── Clock status badge ───────────────────────────────────────
function ClockBadge({ status, since, dense }) {
  const s = KCLOCK[status] || KCLOCK.off;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, background: s.soft, color: s.c,
      fontSize: dense ? 9.5 : 10.5, fontWeight: 700, padding: dense ? '1px 6px' : '2px 8px', borderRadius: 99, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: s.c, flexShrink: 0 }}></span>
      {s.label}
      {since != null && <span style={{ fontFamily: KT.mono, opacity: 0.75, fontWeight: 600 }}>· <LiveDuration since={since} /></span>}
    </span>
  );
}

// ── Fuel gauge ───────────────────────────────────────────────
function FuelGauge({ pct, width = 54, showLabel = true }) {
  if (pct == null) return <span style={{ fontFamily: KT.mono, fontSize: 10.5, fontWeight: 700, color: KT.muteSoft }}>—</span>;
  const c = pct < 25 ? KT.rust : pct < 45 ? KT.warn : KT.green;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width, height: 7, borderRadius: 99, background: KT.surface3, overflow: 'hidden', flexShrink: 0, display: 'block' }}>
        <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: c, borderRadius: 99 }}></span>
      </span>
      {showLabel && <span style={{ fontFamily: KT.mono, fontSize: 10.5, fontWeight: 700, color: c }}>{pct}%</span>}
    </span>
  );
}

// ── Ticket progress bar ──────────────────────────────────────
function TicketProgress({ done, scheduled, height = 7, showLabel = true }) {
  return (
    <div>
      {showLabel && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: KT.muteSoft, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 3 }}>
          <span>Tickets</span>
          <span style={{ fontFamily: KT.mono, textTransform: 'none', letterSpacing: 0, color: KT.ink2 }}>{done}<span style={{ color: KT.muteSoft }}> / {scheduled}</span></span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: scheduled }).map((_, i) => (
          <span key={i} style={{ flex: 1, height, borderRadius: 3, background: i < done ? KT.green : KT.surface3, transition: 'background 0.3s' }}></span>
        ))}
      </div>
    </div>
  );
}

// ── OT risk badge ────────────────────────────────────────────
function OTBadge({ ot, dense }) {
  const map = { ontrack: { c: KT.green, bg: KT.greenSoft, label: 'On track' }, approach: { c: KT.warn, bg: KT.warnSoft, label: 'OT watch' }, will: { c: KT.rust, bg: KT.rustSoft, label: 'Will hit OT' } };
  const s = map[ot.level];
  const hrs = ot.projHrs != null ? `${ot.projHrs}h` : '—';
  return (
    <span title={ot.projHrs != null ? `Projected ${ot.projHrs}h today` : 'no clock data'} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.c,
      fontSize: dense ? 9.5 : 10.5, fontWeight: 800, padding: dense ? '1px 6px' : '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
    }}>
      {s.label}
      <span style={{ fontFamily: KT.mono, fontWeight: 700 }}>{hrs}</span>
    </span>
  );
}

// ── Schedule chip (on-time vs behind) ────────────────────────
function ScheduleChip({ behindMin, dense }) {
  const ahead = behindMin <= 0;
  const ontime = behindMin > 0 && behindMin < 15;
  const c = ahead ? KT.green : ontime ? KT.warn : KT.rust;
  const bg = ahead ? KT.greenSoft : ontime ? KT.warnSoft : KT.rustSoft;
  const label = ahead ? (behindMin <= -5 ? `${-behindMin}m ahead` : 'On time') : `${behindMin}m behind`;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: c, fontSize: dense ? 9.5 : 10.5, fontWeight: 700, padding: dense ? '1px 6px' : '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>{label}</span>
  );
}

// ── Vehicle alert chips ──────────────────────────────────────
function VehicleFlags({ vehicle, dense, max = 99 }) {
  const flags = [];
  if (vehicle.faultCodes.length) flags.push({ k: 'fault', label: vehicle.faultCodes.length > 1 ? `${vehicle.faultCodes.length} fault codes` : vehicle.faultCodes[0], c: KT.rust, bg: KT.rustSoft });
  if (vehicle.regDays < 0) flags.push({ k: 'reg', label: `Reg expired ${-vehicle.regDays}d`, c: KT.rust, bg: KT.rustSoft });
  else if (vehicle.regDays <= 25) flags.push({ k: 'reg', label: `Reg due ${vehicle.regDays}d`, c: KT.warn, bg: KT.warnSoft });
  const hiRepair = vehicle.openRepairs.find((r) => r.priority === 'high');
  if (hiRepair) flags.push({ k: 'rep', label: hiRepair.item, c: KT.rust, bg: KT.rustSoft });
  if (vehicle.mtcOverdueMi) flags.push({ k: 'mtc', label: `Service ${(vehicle.mtcOverdueMi / 1000).toFixed(1)}k mi over`, c: KT.warn, bg: KT.warnSoft });
  if (vehicle.fuel != null && vehicle.fuel < 25) flags.push({ k: 'fuel', label: `Low fuel ${vehicle.fuel}%`, c: KT.rust, bg: KT.rustSoft });
  if (flags.length === 0) return <span style={{ fontSize: 10, color: KT.green, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: 99, background: KT.green }}></span>All clear</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {flags.slice(0, max).map((f, i) => (
        <span key={i} style={{ background: f.bg, color: f.c, fontSize: dense ? 9 : 9.5, fontWeight: 700, fontFamily: KT.mono, padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap', letterSpacing: '0.01em' }}>{f.label}</span>
      ))}
      {flags.length > max && <span style={{ fontSize: 9.5, color: KT.mute, fontWeight: 700 }}>+{flags.length - max}</span>}
    </span>
  );
}

// ── Truck glyph ──────────────────────────────────────────────
function TruckIcon({ size = 14, color = KT.ink2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 7 h10 v8 H2 z" /><path d="M12 10 h4 l3 3 v2 h-7 z" />
      <circle cx="6" cy="17" r="1.6" /><circle cx="16.5" cy="17" r="1.6" />
    </svg>
  );
}

// ── KPI tile ─────────────────────────────────────────────────
function KKpi({ label, value, sub, tone, icon }) {
  const valColor = tone === 'bad' ? KT.rust : tone === 'warn' ? KT.warn : tone === 'good' ? KT.green : KT.ink;
  return (
    <div style={{ flex: 1, minWidth: 120, background: KT.surface, border: `1px solid ${KT.line}`, borderRadius: 10, padding: '10px 12px 9px', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: KT.muteSoft }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: valColor, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: KT.mute }}>{sub}</div>}
    </div>
  );
}

// ── Geo verification badge (per GPS event) ───────────────────
function GeoBadge({ ev, dense }) {
  let c, bg, label;
  if (ev.status === 'yard') { c = KT.mute; bg = KT.surface3; label = 'At yard'; }
  else if (ev.status === 'onsite') { c = KT.green; bg = KT.greenSoft; label = 'On-site'; }
  else if (ev.status === 'offsite') { c = KT.rust; bg = KT.rustSoft; label = `Off-site · ${fmtFt(ev.geoDist)}`; }
  else { c = KT.rust; bg = KT.rustSoft; label = `${ev.known} · ${fmtFt(ev.geoDist)} off`; }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: bg, color: c, fontSize: dense ? 9.5 : 10, fontWeight: 700, padding: dense ? '1px 6px' : '2px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: c, flexShrink: 0 }}></span>{label}
    </span>
  );
}

// ── Phone↔truck separation chip ──────────────────────────────
function PhoneTruckChip({ loc, dense }) {
  const flagged = loc.phoneSplitFlag;
  const c = flagged ? KT.rust : KT.mute;
  return (
    <span title="Distance between the tech's phone and the assigned vehicle" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: dense ? 9.5 : 10.5, fontWeight: 700, color: c, whiteSpace: 'nowrap' }}>
      <PhoneIcon size={9} color={c} /><span style={{ color: KT.muteSoft, fontWeight: 600 }}>↔</span><TruckIcon size={12} color={c} />
      <span style={{ fontFamily: KT.mono }}>{fmtFt(loc.phoneSplitFt)}</span>{flagged && loc.phoneKnown ? <span style={{ fontWeight: 600 }}>· {loc.phoneKnown}</span> : null}
    </span>
  );
}

function PhoneIcon({ size = 10, color = KT.ink2 }) {
  return (
    <svg width={size} height={size * 1.4} viewBox="0 0 12 17" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
      <rect x="1" y="1" width="10" height="15" rx="2" /><line x1="4.6" y1="13" x2="7.4" y2="13" />
    </svg>
  );
}

// ── Location integrity strip (crew card one-liner) ───────────
function LocationStrip({ loc }) {
  if (loc.flags === 0) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: KT.green }}>
        <GeoDot color={KT.green} /> All punches on-site · phone with truck
      </span>
    );
  }
  const bits = [];
  if (loc.offSiteCount) bits.push(`${loc.offSiteCount} off-site`);
  if (loc.hangoutCount) bits.push(`${loc.hangoutCount} at known stop`);
  if (loc.phoneSplitFlag) bits.push(`phone ${fmtFt(loc.phoneSplitFt)} off`);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 700, color: KT.rust }}>
      <GeoDot color={KT.rust} /> {loc.flags} location {loc.flags > 1 ? 'flags' : 'flag'} · <span style={{ fontWeight: 600, color: KT.ink2 }}>{bits.join(' · ')}</span>
    </span>
  );
}
function GeoDot({ color }) {
  return <span style={{ width: 11, height: 11, borderRadius: 99, flexShrink: 0, position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: `1.6px solid ${color}` }}><span style={{ width: 3.5, height: 3.5, borderRadius: 99, background: color }}></span></span>;
}

// ── Time budget (Driving / On-site / Tracked / Yard / Idle) ──
function catColor(cat) { const m = { onsite: KT.green, jobsite: KT.green, driving: KT.blue, lunch: KT.amber, break: KT.plum, tracked: KT.orange, idle: KT.rust, yard: KT.muteSoft }; return m[cat] || KT.muteSoft; }
// aggregated stacked bar (totals by category)
function TimeBudgetBar({ budget, total, height = 8 }) {
  const t = total || BUDGET_CATS.reduce((a, c) => a + (budget[c.k] || 0), 0) || 1;
  return (
    <div style={{ display: 'flex', width: '100%', height, borderRadius: 99, overflow: 'hidden', background: KT.surface3 }}>
      {BUDGET_CATS.map((c) => (budget[c.k] > 0 ? <div key={c.k} title={`${c.label} ${fmtMin(budget[c.k])}`} style={{ width: `${(budget[c.k] / t) * 100}%`, background: c.c }}></div> : null))}
    </div>
  );
}
// chronological ribbon — one slice per visit/drive in the order they happened
function DayRibbon({ loc, height = 10 }) {
  const hh = hhmm;
  const segs = [];
  loc.visits.forEach((v) => segs.push({ t: v.startT, cat: v.category, dur: v.durMin, name: v.name, start: v.startT }));
  (loc.drives || []).forEach((d) => segs.push({ t: d.fromT, cat: 'driving', dur: d.min, name: 'Driving', start: d.fromT }));
  segs.sort((a, b) => a.t - b.t);
  const total = segs.reduce((a, s) => a + s.dur, 0) || 1;
  return (
    <div style={{ display: 'flex', width: '100%', height, borderRadius: 99, overflow: 'hidden', background: KT.surface3 }}>
      {segs.map((s, i) => (
        <div key={i} title={`${hh(s.start)} · ${s.name} · ${fmtMin(s.dur)}`} style={{ width: `${(s.dur / total) * 100}%`, background: catColor(s.cat), borderRight: i < segs.length - 1 ? '1px solid rgba(251,248,241,0.5)' : 'none' }}></div>
      ))}
    </div>
  );
}
function BudgetLegend({ budget, compact, hideZero }) {
  return (
    <div style={{ display: 'flex', gap: compact ? 8 : 13, flexWrap: 'wrap' }}>
      {BUDGET_CATS.filter((c) => !hideZero || budget[c.k] > 0).map((c) => (
        <span key={c.k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: compact ? 9.5 : 10.5, fontWeight: 600, color: KT.ink2 }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: c.c, flexShrink: 0 }}></span>
          {c.label} <b style={{ fontFamily: KT.mono, fontWeight: 700, color: KT.ink }}>{fmtMin(budget[c.k] || 0)}</b>
        </span>
      ))}
    </div>
  );
}

// ── GPS visit timeline (sidebar) ─────────────────────────────
function EventTimeline({ loc }) {
  const hh = hhmm;
  const tag = {
    yard: { l: 'Yard', c: KT.mute, bg: KT.surface3 },
    jobsite: { l: 'Jobsite', c: KT.greenDeep, bg: KT.greenSoft },
    tracked: { l: 'Tracked Location', c: KT.orange, bg: KT.orangeSoft },
    idle: { l: 'Idle / Other', c: KT.rust, bg: KT.rustSoft },
    lunch: { l: 'Lunch', c: KT.amber, bg: KT.amberSoft },
    break: { l: 'Break', c: KT.plum, bg: KT.plumSoft },
  };
  const seq = [];
  loc.visits.forEach((v) => seq.push({ kind: 'visit', t: v.startT, v }));
  (loc.drives || []).forEach((d) => seq.push({ kind: 'drive', t: d.fromT, min: d.min }));
  seq.sort((a, b) => a.t - b.t);
  let n = 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {seq.map((it, i) => {
        if (it.kind === 'drive') return (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0 2px 62px', color: KT.blue, fontSize: 10, fontWeight: 600, fontStyle: 'italic' }}>
            <TruckIcon size={12} color={KT.blue} /> driving {it.min}m
          </div>
        );
        n++; const v = it.v; const tg = tag[v.category];
        const last = i === seq.length - 1;
        return (
          <div key={i} style={{ display: 'flex', gap: 8 }}>
            <div style={{ width: 50, textAlign: 'right', flexShrink: 0, paddingTop: 1 }}>
              <div style={{ fontFamily: KT.mono, fontSize: 9.5, color: KT.ink2, fontWeight: 700 }}>{hh(v.startT)}</div>
              <div style={{ fontFamily: KT.mono, fontSize: 8.5, color: KT.muteSoft }}>{v.endT != null ? hh(v.endT) : 'now'}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ width: 18, height: 18, borderRadius: 99, background: tg.bg, color: tg.c, border: `1.5px solid ${tg.c}`, fontSize: 9.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
              {!last && <span style={{ flex: 1, width: 2, background: KT.line, minHeight: 10 }}></span>}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: KT.ink }}>{v.name}</span>
                <span style={{ background: tg.bg, color: tg.c, fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 5, fontFamily: KT.mono }}>{fmtMin(v.durMin)}</span>
                <span style={{ fontSize: 9.5, color: KT.muteSoft, fontWeight: 600 }}>{tg.l}</span>
                {v.idlingMin > 0 && <span style={{ background: KT.warnSoft, color: KT.warn, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 5 }}>Idling {v.idlingMin}m</span>}
              </div>
              {v.insideFt != null && <div style={{ fontSize: 9.5, color: KT.muteSoft, marginTop: 2 }}>{v.fromCenterFt}ft from center ({v.insideFt}ft inside geofence)</div>}
              {v.insideFt == null && v.fromCenterFt != null && v.category === 'idle' && <div style={{ fontSize: 9.5, color: KT.rust, marginTop: 2, fontWeight: 600 }}>{fmtFt(v.fromCenterFt)} from nearest tracked place</div>}
            </div>
          </div>
        );
      })}
      <div style={{ paddingTop: 2, paddingLeft: 62 }}><PhoneTruckChip loc={loc} dense /></div>
    </div>
  );
}

export {
  CrewGlyph,
  useTick,
  LiveDuration,
  Avatar,
  ClockBadge,
  FuelGauge,
  TicketProgress,
  OTBadge,
  ScheduleChip,
  VehicleFlags,
  TruckIcon,
  PhoneIcon,
  KKpi,
  GeoBadge,
  PhoneTruckChip,
  LocationStrip,
  GeoDot,
  EventTimeline,
  TimeBudgetBar,
  DayRibbon,
  BudgetLegend,
  catColor,
  statusAccent,
};
